import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { defaultTo } from 'lodash';
import React from 'react';

import { useAppForm } from '@/app/lib/tanstack-form';
import { BillingSidebar } from '@/billing/components/billing-sidebar';
import { PaymentMethodSelect } from '@/billing/components/payment-method-select';
import { useCardDetailsForm } from '@/billing/hooks/use-card-details-form';
import { useCheckDetailsForm } from '@/billing/hooks/use-check-details-form';
import { useSetupAutomaticPayments } from '@/billing/queries';
import { Button, ButtonGroup } from '@/common/components/button';
import { CheckboxField, CheckboxGroup } from '@/common/components/checkbox';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@/common/components/dialog';
import { Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { Text } from '@/common/components/text';
import { isNullOrWhitespace } from '@/common/string';
import { getAccountLabel } from '@/shared/accounts';
import { useUserAccounts } from '@/shared/accounts/queries';
import { ContentSection } from '@/shared/components/content-section';
import { NoAccountsYet } from '@/shared/components/empty-states';

export const Route = createFileRoute('/billing/automatic-payments/new')({
  beforeLoad: async ctx => ({
    user: await ctx.context.ensureUser()
  }),
  head: () => ({
    meta: [
      { title: 'Add Automatic Payment | myTalquin' }
    ]
  }),
  component: AddAutomaticPaymentPage
});

function AddAutomaticPaymentPage() {
  const { user } = Route.useRouteContext();
  const { accounts } = user;
  const hasAnyAccounts = accounts.length > 0;
  const { data: accountDetails } = useUserAccounts();
  const [validationDialog, setValidationDialog] = React.useState<{ title: string; description: string; open: boolean }>({ title: '', description: '', open: false });
  const navigate = useNavigate();

  const accountsWithoutAutomaticPayments = accountDetails.filter(account => !account.automatic_payment);

  const setupAutomaticPaymentsMutation = useSetupAutomaticPayments({
    onSuccess: async () => {
      await navigate({ to: '/billing/automatic-payments' });
    }
  });

  const parentForm = useAppForm({
    defaultValues: {
      paymentType: '',
      accounts: accountsWithoutAutomaticPayments.map(account => ({
        accountNumber: account.account_number,
        selected: false
      }))
    }
  });

  const newCardDetailsForm = useCardDetailsForm();
  const newCheckDetailsForm = useCheckDetailsForm();

  const handleSaveClick = async () => {
    await parentForm.handleSubmit();
    const { paymentType, accounts } = parentForm.state.values;
    const selectedAccounts = accounts.filter(account => account.selected);

    if (selectedAccounts.length === 0) {
      setValidationDialog({
        title: 'No Accounts Selected',
        description: 'Please select at least one account',
        open: true
      });
      return;
    }

    if (isNullOrWhitespace(paymentType)) {
      setValidationDialog({
        title: 'No Payment Method Selected',
        description: 'Please select a payment method',
        open: true
      });
      return;
    }

    const accountNumbers = selectedAccounts.map(account => account.accountNumber);

    if (paymentType.startsWith('saved:')) {
      const id = paymentType.replace('saved:', '');
      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'saved',
        paymentMethod: { id }
      });

      return;
    }

    if (paymentType === 'new:card') {
      await newCardDetailsForm.handleSubmit();
      if (!newCardDetailsForm.state.isValid) return;

      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'card',
        paymentMethod: newCardDetailsForm.state.values
      });

      return;
    }

    if (paymentType === 'new:check') {
      await newCheckDetailsForm.handleSubmit();
      if (!newCheckDetailsForm.state.isValid) return;

      await setupAutomaticPaymentsMutation.mutateAsync({
        accountNumbers,
        paymentMethodSelectionType: 'check',
        paymentMethod: newCheckDetailsForm.state.values
      });

      return;
    }
  };

  const closeValidationDialog = () => {
    setValidationDialog({ title: '', description: '', open: false });
  };

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <BillingSidebar active="automatic-payments"/>
      <ContentSection bg="white">
        <Heading>Add Automatic Payment</Heading>

        {hasAnyAccounts ? (
          <>
            <Text>
              Select the account(s) which you would like to add this automatic payment and then “Select Payment Method” to either choose a Payment Method or enter a new one.
            </Text>

            <div className="mx-auto max-w-2xl space-y-16 sm:space-y-20 lg:mx-0 lg:max-w-none">
              <div>
                <dl className="mt-6 divide-y divide-gray-100 border-y border-gray-100 text-sm/6">
                  <div className="py-6 sm:flex">
                    <dt className="font-medium text-gray-900 sm:w-64 sm:flex-none sm:pr-6">Accounts</dt>
                    <dd className="mt-1 flex justify-between gap-x-6 sm:mt-0 sm:flex-auto">
                      <parentForm.AppField name="accounts" mode="array">
                        {accountsField => (
                          <CheckboxGroup>
                            {accountsField.state.value.map((value, idx) => {
                              const account = accounts.find(it => it.account_number === value.accountNumber)!;

                              return (
                                <parentForm.AppField name={`accounts[${idx}].selected`} key={value.accountNumber}>
                                  {accountField => (
                                    <CheckboxField>
                                      <accountField.FormCheckbox />
                                      <Label>{getAccountLabel(account)}</Label>
                                    </CheckboxField>
                                  )}
                                </parentForm.AppField>
                              );
                            })}
                          </CheckboxGroup>
                        )}
                      </parentForm.AppField>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <h3 className="font-bold mt-4">
              Payment Method
            </h3>

            <parentForm.AppField name="paymentType">
              {({ state: { value }, handleChange }) => (
                <PaymentMethodSelect
                  className="my-4"
                  paymentMethod={value}
                  onPaymentMethodChange={handleChange}
                  cardDetailsForm={newCardDetailsForm}
                  checkDetailsForm={newCheckDetailsForm}
                />
              )}
            </parentForm.AppField>

            <Text>
              By submitting Automatic Payment online: I authorize Talquin to draw monthly bank drafts on my bank account and/or credit or debit card for the payment of my monthly electric, water, and/or wastewater bill(s). I understand I can discontinue participation in Automatic Payment by cancelling in myTalquin web or app prior to the next billing date. Talquin can terminate this agreement for any reason including account credit history or returned payments.
            </Text>

            <Text className="mt-2">
              I understand if funds are unavailable at the time of payment, the amount will be charged back to my account(s) and a fee will be assessed.
            </Text>

            <ButtonGroup>
              <Button to="/billing/automatic-payments" plain>Cancel</Button>
              <Button
                onClick={handleSaveClick}
                disabled={setupAutomaticPaymentsMutation.isPending}
              >
                {setupAutomaticPaymentsMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </ButtonGroup>
          </>
        ) : (
          <NoAccountsYet/>
        )}
      </ContentSection>

      <Dialog open={validationDialog.open} onClose={closeValidationDialog}>
        <DialogTitle>{defaultTo(validationDialog.title, 'Validation Error')}</DialogTitle>
        <DialogDescription>{defaultTo(validationDialog.description, 'Please review your submission and try again.')}</DialogDescription>
        <DialogActions>
          <Button plain onClick={closeValidationDialog}>Ok</Button>
        </DialogActions>
      </Dialog>
    </main>
  );
}
